import Layer from './Layer';
import Tool from './Tool';
import { createTransformToolChain, createNewNodeToolChain } from './toolChains';
import Rect from './Rect';
import Vector2 from './Vector2';
import { observable } from 'mobx';
import ConnectionLayer from './ConnectionLayer';
import Scene from './Scene';
import { renderConnectionLayer, renderNodeLayer } from './Render';
import NodeLayer from './NodeLayer';

export type ErrorType = {
  title: string;
  value: string;
};

export type InferenceMode = 'StartLayer' | 'SelectedLayer' | 'None';

export default class Editor extends Scene {
  @observable
  private _selectedLayer: Layer;
  private _canvaSize: any;

  @observable
  private _currentTool = createTransformToolChain(this);

  @observable
  private _cursor = 'default';

  @observable
  private _error: ErrorType = {
    title: '',
    value: '',
  };

  public get canvaSize() {
    return this._canvaSize;
  }

  public setCanvaSize(canva: any) {
    this._canvaSize = canva;
  }

  @observable
  private _inferenceMode: InferenceMode = 'None';

  public get inferenceMode(): InferenceMode {
    return this._inferenceMode;
  }

  public setInferenceMode(inference: InferenceMode) {
    this._inferenceMode = inference;
  }

  public get error(): ErrorType {
    return this._error;
  }

  public setError(err: ErrorType) {
    if (err == null) {
      err = {
        title: '',
        value: '',
      };
    }
    this._error = err;
  }

  public get cursor(): string {
    return this._cursor;
  }

  public setCursor(value: string) {
    if (this._cursor == value) {
      return;
    }
    this._cursor = value;
  }

  public get currentTool(): Tool {
    return this._currentTool;
  }

  public setCurrentTool(tool: Tool) {
    if (tool.name == this.currentTool.name) {
      return;
    }

    if (this._currentTool != null) {
      this._currentTool.finalize();
    }
    this._currentTool = tool;
    this._currentTool.initialize();
  }

  public get selectedLayer(): Layer {
    return this._selectedLayer;
  }

  public setSelectedLayer(layer: Layer) {
    if (layer != this._selectedLayer) {
      this._selectedLayer = layer;
    }
  }

  public getSelectionBoundingRect(): Rect {
    return this.selectedLayer == null
      ? new Rect()
      : this.selectedLayer.boundingRect;
  }

  private drawSelection(context: CanvasRenderingContext2D) {
    if (
      this.selectedLayer == null ||
      this.selectedLayer instanceof ConnectionLayer
    ) {
      return;
    }

    var blue = 'rgb(155, 66, 227)';

    context.strokeStyle = blue;
    context.lineWidth = 2 * window.devicePixelRatio;

    var rect = this.camera.transformRectWorldToScreen(
      this.getSelectionBoundingRect()
    );

    var p0 = rect.position;
    var p1 = rect.position.add(new Vector2(rect.size.x, 0));
    var p2 = rect.position.add(rect.size);
    var p3 = rect.position.add(new Vector2(0, rect.size.y));

    context.beginPath();
    context.moveTo(p0.x, p0.y);
    context.lineTo(p1.x, p1.y);
    context.lineTo(p2.x, p2.y);
    context.lineTo(p3.x, p3.y);
    context.closePath();
    context.stroke();
  }

  drawLayerGroup(context: CanvasRenderingContext2D) {
    let layers = this.rootLayerGroup.layers;
    for (var i = 0; i < layers.length; i++) {
      var layer = layers[i];
      if (layer instanceof ConnectionLayer) {
        renderConnectionLayer(layer, context);
      }
    }

    for (var i = 0; i < layers.length; i++) {
      var layer = layers[i];
      if (layer instanceof NodeLayer) {
        renderNodeLayer(layer, context);
      }
    }
  }

  draw(context: CanvasRenderingContext2D) {
    this.drawLayerGroup(context);
    this.drawSelection(context);
    this._currentTool.draw(context);
  }
}
